/**
 * Checkbox component
 * Supports both Tailwind UI and DataTable variants
 */

import React from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  onCheckedChange?: (checked: boolean) => void
  /**
   * Variant: 'ui' for Tailwind-styled checkboxes, 'datatable' for DataTable-styled checkboxes
   * @default 'ui'
   */
  variant?: 'ui' | 'datatable'
  /**
   * Whether the checkbox shows an indeterminate state (partial selection)
   * Only for datatable variant
   */
  indeterminate?: boolean
  /**
   * Whether the checkbox is only visible on hover (only for datatable variant)
   * When true, checkbox has low opacity until row is hovered
   * @default false
   */
  onHoverVisible?: boolean
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      checked,
      onCheckedChange,
      onChange,
      variant = 'ui',
      indeterminate = false,
      onHoverVisible = false,
      ...props
    },
    ref
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null)

    // Merge refs
    React.useImperativeHandle(ref, () => inputRef.current!)

    // Set indeterminate state (datatable variant only)
    React.useEffect(() => {
      if (inputRef.current && variant === 'datatable') {
        inputRef.current.indeterminate = indeterminate
      }
    }, [indeterminate, variant])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e)
      onCheckedChange?.(e.target.checked)
    }

    // DataTable variant uses native checkbox with CSS styling
    if (variant === 'datatable') {
      const wrapperClasses = [
        'data-table__checkbox',
        onHoverVisible && 'data-table__checkbox--hover-visible',
      ]
        .filter(Boolean)
        .join(' ')

      return (
        <label
          className={wrapperClasses}
          onClick={(event) => {
            event.stopPropagation()
          }}
        >
          <input
            ref={inputRef}
            type="checkbox"
            checked={checked}
            onChange={handleChange}
            className={className}
            {...props}
          />
        </label>
      )
    }

    // UI variant uses Tailwind with custom styling
    return (
      <div className="relative inline-flex items-center">
        <input
          type="checkbox"
          ref={inputRef}
          checked={checked}
          onChange={handleChange}
          className="sr-only peer"
          {...props}
        />
        <div
          className={cn(
            'h-4 w-4 shrink-0 rounded-sm border border-gray-300 cursor-pointer',
            'peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500',
            'peer-disabled:cursor-not-allowed peer-disabled:opacity-50',
            'peer-checked:bg-blue-500 peer-checked:border-blue-500',
            'dark:border-gray-600 dark:peer-checked:bg-blue-600',
            className
          )}
          onClick={() => {
            const input = inputRef.current
            if (input && !props.disabled) {
              input.click()
            }
          }}
        >
          {checked && <Check className="h-3 w-3 text-white absolute top-0.5 left-0.5" />}
        </div>
      </div>
    )
  }
)
Checkbox.displayName = 'Checkbox'
